import React, { useState, forwardRef, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { TextField, Grid, Select, MenuItem, FormControl, InputLabel, FormHelperText } from '@mui/material'

import { Logger, LoggerTypes, removeSpace } from '../../../../utils'
import { scopeTypes, textFieldTypes, variableTypes, variantTypes } from '../../../../constants'
import { iconNames } from '../../../icons'
import { FormTitle } from '../../../forms'
import { ButtonFormula } from '../../../buttons'

const defaultValue = {
  id: null,
  name: null,
  description: null,
  value: '0',
  type: 'NUMBER', // variableTypes.NUMBER
  unit: null
}

/**
 * Generic form to create or edit an attribute `parameter`
 *
 * @param {func} setOpenDialog State function used to close the container popin.
 * @param {string} title Title of the formulary (can change if it's a create or an update).
 * @param {array<object>} parameters Array of parameters objects.
 * @param {object} parameter Location attribute object. (used only while editing an existing object).
 * @param {func} dispatchAction Function to call one the form has been submit.
 * @param {object} errors Error object that contains the mistakes fields by fields.
 */
export const AttributeParameterForm = forwardRef(
  ({ setOpenDialog, title, parameters, parameter, dispatchAction, errors }, ref) => {
    const { t } = useTranslation()

    const [id] = useState(parameter.id || defaultValue.id)
    const [name, setName] = useState(parameter.name || defaultValue.name)
    const [description, setDescription] = useState(parameter.description || defaultValue.description)
    const [value, setValue] = useState(parameter.value || defaultValue.value)
    const [type, setType] = useState(parameter.type || defaultValue.type)
    const [unit, setUnit] = useState(parameter.unit || defaultValue.unit)

    const isDisabled = parameter?.scope === scopeTypes.IMPORT

    const onChangeName = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onChangeName()', { name: _value })
      setName(removeSpace(_value))
    }

    const onChangeDescription = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onChangeDescription()', { description: _value })
      setDescription(_value)
    }

    const onChangeValue = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onChangeValue()', { value: _value })
      setValue(_value)
    }

    const onValueFormulaValidated = (_value, equation, formula) => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onValueFormulaValidated()', {
        _value,
        equation,
        formula
      })

      if (_value) {
        setValue(_value)
      }
    }

    const onChangeType = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onChangeType()', { type: _value })
      setType(_value)
    }

    const onChangeUnit = (e) => {
      const _value = e.target.value
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onChangeUnit()', { unit: _value })
      setUnit(_value)
    }

    const onCloseDialog = () => {
      Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onCloseDialog()')
      setOpenDialog(false)
    }

    // ----

    useImperativeHandle(ref, () => ({
      async onSubmitForm() {
        const newParameter = {
          name: name === '' ? null : name,
          description: description === '' ? null : description,
          value: value === '' ? null : value,
          type: type === '' ? null : type,
          unit: unit === '' ? null : unit
        }

        Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onSubmitForm()', {
          newParameter,
          parameter,
          dispatchAction
        })

        const _onSuccess = (res) => {
          Logger.trace(LoggerTypes.CONTROLLER, 'AttributeParameterForm.onSubmitForm()._onSuccess()', { res })

          // -- close popin only in case of success
          onCloseDialog()
        }

        dispatchAction(newParameter, id, _onSuccess)
      }
    }))

    Logger.trace(LoggerTypes.CONTAINER, 'AttributeParameterForm.render()', {
      name,
      description,
      value,
      unit,
      type,
      dispatchAction
    })

    return (
      <div className="AttributeForm AttributeParameterForm">
        <FormTitle title={title} icon={iconNames.PARAMETER} iconSize={22} />
        {isDisabled ? <div className="importGlobal">{t('global.title')}</div> : ''}
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2} style={{ float: 'left' }}>
                {/* name */}
                <Grid item xs={12} sm={12}>
                  <TextField
                    type={textFieldTypes.TEXT}
                    name="name"
                    id="name"
                    value={name}
                    variant={variantTypes.OUTLINED}
                    disabled={isDisabled}
                    label={t('attributes.parameters.name.label')}
                    className="common_fullwidth"
                    onChange={onChangeName}
                    placeholder={t('attributes.parameters.name.placeholder')}
                    error={Boolean(errors && errors?.name)}
                    helperText={(errors && errors?.name) || ''}
                  />
                </Grid>

                {/*  type  */}
                <Grid item xs={12} sm={12} className="common_fullwidth">
                  <FormControl error={Boolean(errors && errors?.type)} className="common_fullwidth">
                    <InputLabel style={{ backgroundColor: 'white', padding: '0 5px' }}>
                      {t('attributes.parameters.type.label')}
                    </InputLabel>
                    <Select
                      className="common_fullwidth"
                      value={type}
                      disabled={isDisabled}
                      defaultValue={variableTypes.PERCENTAGE}
                      onChange={onChangeType}
                      variant={variantTypes.OUTLINED}
                    >
                      <MenuItem value={variableTypes.NUMBER}>{t('attributes.parameters.type.number')}</MenuItem>
                      <MenuItem value={variableTypes.PERCENTAGE}>{t('attributes.parameters.type.percentage')}</MenuItem>
                    </Select>
                    <FormHelperText>{(errors && errors?.type) || ''}</FormHelperText>
                  </FormControl>
                </Grid>

                {/*  value / unit  */}
                <Grid item xs={12} sm={12} className="common_fullwidth">
                  {/*  value  */}
                  <TextField
                    type={textFieldTypes.NUMBER}
                    name="value"
                    id="value"
                    value={value}
                    label={t('attributes.parameters.value.label')}
                    onChange={onChangeValue}
                    style={{ width: '188px', marginRight: '10px' }}
                    placeholder={t('attributes.parameters.value.placeholder')}
                    variant={variantTypes.OUTLINED}
                    error={Boolean(errors && errors?.value)}
                    helperText={(errors && errors?.value) || ''}
                  />
                  <ButtonFormula
                    forwardedRef={ref}
                    parameters={parameters}
                    onValidate={onValueFormulaValidated}
                    tooltip={t('attributes.parameters.value.formula_tooltip')}
                  />

                  {/*  unit  */}
                  <TextField
                    type={textFieldTypes.STRING}
                    name="unit"
                    id="unit"
                    disabled={type === variableTypes.PERCENTAGE || isDisabled}
                    value={type === variableTypes.PERCENTAGE ? '%' : unit}
                    InputLabelProps={{ shrink: true }}
                    label={t('attributes.parameters.unit.label')}
                    onChange={onChangeUnit}
                    style={{ width: '150px', marginLeft: '14px' }}
                    placeholder={t('attributes.parameters.unit.placeholder')}
                    variant={variantTypes.OUTLINED}
                    error={Boolean(errors && errors?.unit)}
                    helperText={(errors && errors?.unit) || ''}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              {/* description */}
              <Grid item sm={12} xs={12} className="description-container">
                <TextField
                  type={textFieldTypes.TEXT}
                  name="description"
                  id="description"
                  label={t('attributes.parameters.description.label')}
                  onChange={onChangeDescription}
                  value={description}
                  variant={variantTypes.OUTLINED}
                  multiline={true}
                  disabled={isDisabled}
                  rows={7.3}
                  className="common_fullwidth"
                  placeholder={t('attributes.parameters.description.placeholder')}
                  error={Boolean(errors && errors?.description)}
                  helperText={(errors && errors?.description) || ''}
                />
              </Grid>
            </Grid>
            {errors && errors?.generic ? (
              <div className="MuiFormHelperText-root Mui-error MuiFormHelperText-sizeMedium MuiFormHelperText-contained errorField">
                {errors.generic}
              </div>
            ) : null}
          </Grid>
        </form>
      </div>
    )
  }
)

AttributeParameterForm.displayName = 'AttributeParameterForm'

AttributeParameterForm.propTypes = {
  setOpenDialog: PropTypes.func.isRequired,
  dispatchAction: PropTypes.func.isRequired,
  title: PropTypes.string,
  parameters: PropTypes.arrayOf(PropTypes.object),
  parameter: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    value: PropTypes.string,
    type: PropTypes.string,
    unit: PropTypes.string,
    scope: PropTypes.string
  }).isRequired,
  errors: PropTypes.shape({
    name: PropTypes.any,
    description: PropTypes.any,
    value: PropTypes.any,
    type: PropTypes.any,
    unit: PropTypes.any,
    generic: PropTypes.any
  })
}

AttributeParameterForm.defaultProps = {
  setOpenDialog: () => {},
  dispatchAction: null,
  title: null,
  parameters: [],
  parameter: defaultValue,
  errors: null
}
